import React, { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

import {
  ReelBarsSlide,
  ReelCoverSlide,
  ReelDonutSlide,
  ReelKpisSlide,
  ReelLineupSlide,
  ReelNextStepSlide,
  ReelQuoteSlide,
  ReelSlide,
  ReelStimulus,
  ResolvedStudyReel,
} from '@digital-twins/shared-types/reporting'

/**
 * The reel's pages, built from `ResolvedStudyReel` (`study-reel.v1`).
 *
 * Ported from the Gate 1 review gallery (`apps/python-api/scripts/study_reel/reel.js`),
 * which the owner approved. Every string comes from the resolved reel; nothing is parsed
 * or rewritten here. A `lineup` slide with `layout: "one_per_slide"` becomes one page per
 * stimulus; every other slide is one page.
 */

/** Every user-facing string the player prints that the reel itself does not carry. */
export interface ReelPlayerLabels {
  /** The stage's accessible name. */
  region: (studyTitle: string) => string
  /** The progress bar's accessible name. */
  slider: string
  /** The progress bar's value text. */
  slidePosition: (current: number, total: number, title: string) => string
  previous: string
  next: string
  autoplay: string
  pause: string
  /** A one-per-slide line-up page's position, after its eyebrow. */
  lineupPosition: (current: number, total: number) => string
  /** The tag on a video tile. */
  video: string
  /** The line under a quote that names the stimulus it is about. */
  aboutStimulus: (name: string) => string
  /** The respondent count in the top bar. */
  respondents: (count: number) => string
  /** The next-step page's button, shown only when the host passes `onOpenReport`. */
  openReport: string
}

export const DEFAULT_REEL_PLAYER_LABELS: ReelPlayerLabels = {
  region: (studyTitle) => `${studyTitle} reel`,
  slider: 'Slide',
  slidePosition: (current, total, title) => `Slide ${current} of ${total}: ${title}`,
  previous: 'Previous slide',
  next: 'Next slide',
  autoplay: 'Autoplay',
  pause: 'Pause',
  lineupPosition: (current, total) => `${current} of ${total}`,
  video: 'Video',
  aboutStimulus: (name) => `About ${name}`,
  respondents: (count) => `n=${count}`,
  openReport: 'Open the full report',
}

/** Slide surfaces. `paper` is the one light page; the chrome follows it. */
export type ReelSurface = 'ink' | 'tone' | 'cover' | 'paper' | 'end' | 'solo' | 'photo'

export interface ReelPage {
  key: string
  kind: ReelSlide['kind']
  /** The name under the stage and in the progress bar's value text. */
  title: string
  surface: ReelSurface
  content: React.ReactNode
  sourceLabel: string
}

interface PageContext {
  labels: ReelPlayerLabels
  stimuliById: Map<string, ReelStimulus>
  onOpenReport?: () => void
}

const TILE_TONES = 6

const DONUT_TONES = 6

const DONUT_CIRCUMFERENCE = 2 * Math.PI * 40

const DONUT_GAP = 0.8

function isLoadableUrl(url: string | null | undefined): url is string {
  if (typeof url !== 'string' || url.length === 0) {
    return false
  }
  try {
    const parsed = new URL(url, typeof window === 'undefined' ? 'http://localhost' : window.location.href)
    return ['http:', 'https:', 'data:', 'blob:'].includes(parsed.protocol)
  } catch {
    return false
  }
}

interface ReelMediaProps {
  stimulus: ReelStimulus
  className?: string
}

/** The stimulus itself, by `mediaKind`; a missing URL or a load error leaves the tile's tone. */
function ReelMedia(props: ReelMediaProps) {
  const { stimulus, className } = props

  const [hasFailed, setHasFailed] = useState(false)

  const url = stimulus.url
  if (hasFailed || !isLoadableUrl(url)) {
    return null
  }
  if (stimulus.mediaKind === 'image') {
    return (
      <img
        className={className}
        src={url}
        alt={stimulus.name}
        draggable={false}
        onError={() => setHasFailed(true)}
        data-testid="reel-media-image"
      />
    )
  }
  if (stimulus.mediaKind === 'video') {
    return (
      <video
        className={className}
        src={url}
        preload="metadata"
        muted
        playsInline
        onError={() => setHasFailed(true)}
        data-testid="reel-media-video"
      />
    )
  }
  return null
}

function Heading(props: { eyebrow: string; title: string; size?: 'xl' }) {
  const { eyebrow, title, size } = props

  return (
    <>
      <span className="reel-eb">{eyebrow}</span>
      <h3 className={cn('reel-h', size === 'xl' && 'reel-h-xl')}>{title}</h3>
    </>
  )
}

/** The cover shows up to this many tested images behind its title. */
const COVER_IMAGE_LIMIT = 4

/** How long each tested image holds the cover before the next fades in. */
const COVER_HOLD_MS = 9000

/**
 * The tested images the cover sits on, in launch order. Empty when the study
 * tested no loadable image.
 */
function coverImageUrls(reel: ResolvedStudyReel): Array<string> {
  if (reel.card.visual.kind !== 'stimuli') {
    return []
  }
  return reel.card.visual.stimuli
    .filter((stimulus) => stimulus.mediaKind === 'image')
    .map((stimulus) => stimulus.url)
    .filter(isLoadableUrl)
    .slice(0, COVER_IMAGE_LIMIT)
}

interface CoverSlideshowProps {
  urls: Array<string>
}

/**
 * The tested images, one at a time and full-bleed behind the cover title: each
 * drifts slowly and cross-fades into the next. Reduced motion holds the first.
 */
function CoverSlideshow(props: CoverSlideshowProps) {
  const { urls } = props

  // `leaving` keeps its pan while it fades out, so it never snaps back.
  const [{ active, leaving }, setShow] = useState<{ active: number; leaving: number | null }>({ active: 0, leaving: null })

  useEffect(() => {
    const isReducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (urls.length < 2 || isReducedMotion) {
      return
    }
    const timer = window.setInterval(
      () => setShow((current) => ({ active: (current.active + 1) % urls.length, leaving: current.active })),
      COVER_HOLD_MS,
    )
    return () => window.clearInterval(timer)
  }, [urls.length])

  return (
    <div className={cn('reel-cover-show', urls.length === 1 && 'is-single')} data-testid="reel-cover-show">
      {urls.map((url, index) => (
        <img
          key={`${index}-${url}`}
          className={cn('reel-cover-img', index === active && 'is-active', index === leaving && 'is-leaving')}
          src={url}
          alt=""
          draggable={false}
        />
      ))}
    </div>
  )
}

function coverPage(slide: ReelCoverSlide, key: string, imageUrls: Array<string>): ReelPage {
  const hasShow = imageUrls.length > 0
  const hasImage = hasShow || isLoadableUrl(slide.backgroundImageUrl)
  const background = hasShow ? (
    <CoverSlideshow urls={imageUrls} />
  ) : hasImage ? (
    <img className="reel-bgimg" src={slide.backgroundImageUrl ?? ''} alt="" draggable={false} />
  ) : null
  const content = (
    <>
      {background}
      {hasImage && <div className="reel-scrim" />}
      <div className="reel-sc">
        <div className="reel-push" />
        {slide.chips.length > 0 && (
          <div className="reel-chips">
            {slide.chips.map((chip, index) => (
              <span key={`${index}-${chip}`} className={cn('reel-chip', index === 0 && 'reel-chip-solid')}>
                {chip}
              </span>
            ))}
          </div>
        )}
        <h3 className="reel-h reel-h-xl">{slide.title}</h3>
      </div>
    </>
  )
  return {
    key,
    kind: slide.kind,
    title: slide.eyebrow,
    surface: hasImage ? 'photo' : 'cover',
    content,
    // The cover's source is the study name itself: nothing to cite under the title.
    sourceLabel: '',
  }
}

function lineupPages(slide: ReelLineupSlide, key: string, context: PageContext): Array<ReelPage> {
  const total = slide.stimuli.length
  if (slide.layout === 'one_per_slide') {
    return slide.stimuli.map((stimulus, index) => {
      const position = context.labels.lineupPosition(index + 1, total)
      return {
        key: `${key}-${stimulus.id}`,
        kind: slide.kind,
        title: `${slide.eyebrow} ${position}`,
        surface: 'solo' as const,
        sourceLabel: slide.source.label,
        content: (
          <>
            <div className={cn('reel-solo-media', `reel-t${index % TILE_TONES}`)}>
              <ReelMedia stimulus={stimulus} className="reel-bgimg" />
            </div>
            <div className="reel-scrim" />
            {stimulus.mediaKind === 'video' && <span className="reel-solo-play" aria-hidden />}
            <div className="reel-sc">
              <span className="reel-eb">{`${slide.eyebrow} · ${position}`}</span>
              <div className="reel-push" />
              <h3 className="reel-h">{stimulus.name}</h3>
              {index === 0 && <p className="reel-sub">{slide.title}</p>}
            </div>
          </>
        ),
      }
    })
  }
  const content = (
    <div className="reel-sc">
      <Heading eyebrow={slide.eyebrow} title={slide.title} />
      <div className={cn('reel-tiles', total > 4 && 'reel-tiles-c3', total === 3 && 'reel-tiles-odd')}>
        {slide.stimuli.map((stimulus, index) => (
          <div
            key={stimulus.id}
            className={cn('reel-tile', `reel-t${index % TILE_TONES}`, isLoadableUrl(stimulus.url) && 'reel-has-media')}
            data-testid={`reel-lineup-tile-${index}`}
          >
            <ReelMedia stimulus={stimulus} />
            {stimulus.mediaKind === 'video' && (
              <>
                <span className="reel-play" aria-hidden />
                <span className="reel-kind">{context.labels.video}</span>
              </>
            )}
            <b>{stimulus.name}</b>
          </div>
        ))}
      </div>
    </div>
  )
  return [{ key, kind: slide.kind, title: slide.eyebrow, surface: 'ink', content, sourceLabel: slide.source.label }]
}

function barsPage(slide: ReelBarsSlide, key: string): ReelPage {
  const content = (
    <div className="reel-sc">
      <Heading eyebrow={slide.eyebrow} title={slide.title} />
      {slide.questionText && <p className="reel-qtext">{slide.questionText}</p>}
      <div className="reel-bars">
        {slide.bars.map((bar, index) => {
          const share = slide.scaleMax > 0 ? Math.max(0, Math.min(100, (bar.figure.value / slide.scaleMax) * 100)) : 0
          const fillStyle = { '--reel-w': `${share.toFixed(2)}%` } as React.CSSProperties
          return (
            <div key={bar.id} className={cn('reel-bar', bar.isTop && 'reel-bar-win')} data-testid={`reel-bar-${index}`}>
              <div className="reel-bar-row">
                <span>{bar.figure.label}</span>
                <b>{bar.figure.display}</b>
              </div>
              <div className="reel-bar-trk">
                <div className="reel-bar-fil" style={fillStyle} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
  return { key, kind: slide.kind, title: slide.eyebrow, surface: 'paper', content, sourceLabel: slide.source.label }
}

function donutPage(slide: ReelDonutSlide, key: string): ReelPage {
  // Segment values are shares of 100 (the contract carries no total); the rest stays as the faint track.
  let start = 0
  const rings = slide.segments.map((segment, index) => {
    const fraction = Math.max(0, segment.figure.value) / 100
    const length = Math.max(fraction * DONUT_CIRCUMFERENCE - DONUT_GAP, 0.5)
    const ring = (
      <circle
        key={segment.id}
        cx={50}
        cy={50}
        r={40}
        className={`reel-donut-c${index % DONUT_TONES}`}
        strokeDasharray={`${length} ${DONUT_CIRCUMFERENCE - length}`}
        strokeDashoffset={-start}
      />
    )
    start += fraction * DONUT_CIRCUMFERENCE
    return ring
  })
  const top = slide.segments.find((segment) => segment.isTop)
  const content = (
    <div className="reel-sc">
      <Heading eyebrow={slide.eyebrow} title={slide.title} />
      {slide.questionText && <p className="reel-qtext">{`“${slide.questionText}”`}</p>}
      <div className="reel-donut-wrap">
        <div className="reel-donut">
          <svg viewBox="0 0 100 100" aria-hidden>
            <circle cx={50} cy={50} r={40} className="reel-donut-track" />
            {rings}
          </svg>
          {top && (
            <div className="reel-donut-mid">
              <b>{top.figure.display}</b>
              <span>{top.figure.label}</span>
            </div>
          )}
        </div>
        <ul className="reel-legend">
          {slide.segments.map((segment, index) => (
            <li key={segment.id} className={cn(segment.isTop && 'reel-legend-top')} data-testid={`reel-donut-legend-${index}`}>
              <i className={`reel-donut-sw${index % DONUT_TONES}`} />
              <span>{segment.figure.label}</span>
              <b>{segment.figure.display}</b>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
  return { key, kind: slide.kind, title: slide.eyebrow, surface: 'ink', content, sourceLabel: slide.source.label }
}

function kpisPage(slide: ReelKpisSlide, key: string): ReelPage {
  const content = (
    <div className="reel-sc">
      <Heading eyebrow={slide.eyebrow} title={slide.title} />
      <div className="reel-kpis">
        {slide.kpis.map((kpi, index) => (
          <div key={kpi.id} className="reel-kpi" data-testid={`reel-kpi-${index}`}>
            <b>{kpi.figure.display}</b>
            <span>{kpi.figure.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
  return { key, kind: slide.kind, title: slide.eyebrow, surface: 'tone', content, sourceLabel: slide.source.label }
}

function quotePage(slide: ReelQuoteSlide, key: string, context: PageContext): ReelPage {
  const about = slide.aboutStimulusId ? context.stimuliById.get(slide.aboutStimulusId) : undefined
  // The quote sits right under the eyebrow (the gallery pushed it to the bottom and left the top half empty).
  const content = (
    <div className="reel-sc">
      <span className="reel-eb">{slide.eyebrow}</span>
      <div className="reel-quote-body">
        <div className="reel-qmark" aria-hidden>
          “
        </div>
        <blockquote className="reel-quote" data-testid="reel-quote-text">
          {slide.text}
        </blockquote>
        {about && <div className="reel-cite">{context.labels.aboutStimulus(about.name)}</div>}
      </div>
    </div>
  )
  return { key, kind: slide.kind, title: slide.eyebrow, surface: 'ink', content, sourceLabel: slide.source.label }
}

function nextStepPage(slide: ReelNextStepSlide, key: string, context: PageContext): ReelPage {
  const { onOpenReport } = context
  const content = (
    <div className="reel-sc">
      <Heading eyebrow={slide.eyebrow} title={slide.title} />
      <ol className="reel-steps">
        {slide.steps.map((step) => (
          <li key={step.id}>
            <span>
              {step.title}
              {step.detail && <small>{step.detail}</small>}
            </span>
          </li>
        ))}
      </ol>
      {onOpenReport && (
        <button type="button" className="reel-cta" onClick={onOpenReport} data-testid="reel-open-report">
          {context.labels.openReport}
        </button>
      )}
    </div>
  )
  return { key, kind: slide.kind, title: slide.eyebrow, surface: 'end', content, sourceLabel: slide.source.label }
}

/** Stimuli by id, for a quote's `aboutStimulusId`. */
function indexStimuli(reel: ResolvedStudyReel): Map<string, ReelStimulus> {
  const byId = new Map<string, ReelStimulus>()
  if (reel.card.visual.kind === 'stimuli') {
    reel.card.visual.stimuli.forEach((stimulus) => byId.set(stimulus.id, stimulus))
  }
  reel.slides.forEach((slide) => {
    if (slide.kind === 'lineup') {
      slide.stimuli.forEach((stimulus) => byId.set(stimulus.id, stimulus))
    }
  })
  return byId
}

export function buildReelPages(
  reel: ResolvedStudyReel,
  labels: ReelPlayerLabels,
  onOpenReport?: () => void,
): Array<ReelPage> {
  const context: PageContext = { labels, stimuliById: indexStimuli(reel), onOpenReport }
  const coverImages = coverImageUrls(reel)
  return reel.slides.flatMap((slide, index) => {
    const key = `${index}-${slide.kind}`
    switch (slide.kind) {
      case 'cover':
        return [coverPage(slide, key, coverImages)]
      case 'lineup':
        return lineupPages(slide, key, context)
      case 'bars':
        return [barsPage(slide, key)]
      case 'donut':
        return [donutPage(slide, key)]
      case 'kpis':
        return [kpisPage(slide, key)]
      case 'quote':
        return [quotePage(slide, key, context)]
      case 'next_step':
        return [nextStepPage(slide, key, context)]
    }
  })
}
